import { useEffect, useMemo, useState } from 'react'
import {
  useGetTransferInfoQuery,
  useGetV1WalletTransferTypesQuery,
  useGetWalletInfoQuery,
} from 'core/query/yapi/hk-web/HotcoinControllerV4/query'
import { useGetMarginIsolatedTradeTypesQuery } from 'core/query/yapi/hk-web/MarginIsolated/query'
import { usePostV2TransferMutation } from 'core/query/yapi/hk-web/Common/query'
import notification from '@/components/notification'
import { spliceDecimal } from 'core/utils/index'
interface AccountTypeItem {
  value: number
  label: string
}
type TTransferProps = {
  accountTypeList: AccountTypeItem[] //账户类型
  amountNonZero: string //划转数量不能为空和0
  notEnoughBalance: string
}
const useTransferData = (localeMap: TTransferProps) => {
  const [source, setSource] = useState<string | number>('') // 转出账户
  const [target, setTarget] = useState<string | number>('') // 转入账户
  const [type, setType] = useState<string | number>('') // 划转方向
  const [amount, setAmount] = useState<string | number>(0) // 数量
  const [tradeId, setTradeId] = useState<number | null>(null) // 交易对id
  const [coinId, setCoinId] = useState<number | null>(null) // 币种Id
  // 账户类型列表
  const accountTypeList = localeMap.accountTypeList
  // 用于在弹窗打开时强制获取新数据
  const refetchKey = useMemo(() => Math.random(), [])

  /**
   * 获取所有可划转币种
   * @param type 划转方向
   * @param tradeId 交易对id 仅当 source/target 为 逐仓杠杆账户时有值
   */
  const { data: coinListData } = useGetTransferInfoQuery({ type, tradeId, refetchKey }, { enabled: !!type })
  /**
   * 获取资金划转类型列表
   * @param coinId 当前选中币种
   * @param currency symbol 币种名称 如USDT
   */
  const { data: walletTransferType } = useGetV1WalletTransferTypesQuery({ coinId, refetchKey })
  /**
   * 获取资产信息
   * @param coinId 当前选中币种
   * @param type 划转方向
   * @walletType 转出账户
   */
  const { data: walletInfoData, isSuccess: walletInfoDataSuccess } = useGetWalletInfoQuery(
    {
      coinId,
      type,
      walletType: source,
      refetchKey,
      tradeId,
    },
    { enabled: !!type }
  )
  /**
   * 初始化资产信息
   */
  const walletInfo = useMemo(() => {
    if (walletInfoData && walletInfoDataSuccess) {
      return walletInfoData?.data?.data
    }
  }, [walletInfoData, walletInfoDataSuccess])

  /**
   * 获取逐仓交易对
   */
  const { data: tradeListData } = useGetMarginIsolatedTradeTypesQuery()
  const tradeList = useMemo(() => {
    if (tradeListData?.data?.data) {
      return tradeListData.data.data.map((item) => {
        return {
          ...item,
          label: `${item.base}/${item.quote}`,
          value: item.tradeId,
          disabled:
            target === 7
              ? !item.baseTransferinable || !item.quoteTransferinable
              : !item.baseTransferoutable || !item.quoteTransferoutable,
        }
      })
    }
  }, [tradeListData, target])

  // 设置划转方向
  useEffect(() => {
    const walletTransferTypeList = walletTransferType?.data?.data
    if (walletTransferTypeList) {
      const transferObj = walletTransferTypeList.find((item) => source == item?.walletType?.walletType)
      if (!transferObj || !transferObj.transferTypes) return
      const targetObj = transferObj?.transferTypes.find((item) => target == item?.targetWallet)
      setType(Number(targetObj?.transferType))
      setAmount('')
    }
  }, [walletTransferType, source, target])

  // 初始化转入转出数据集合
  const { sourceList, targetList } = useMemo(() => {
    const walletTransferTypeList = walletTransferType?.data?.data
    if (walletTransferTypeList) {
      // 筛选出 walletTransferType的元素
      const sourceFromWalletTransferTypeList = walletTransferTypeList.filter((item) => {
        const filter = item.transferTypes?.find((subItem) => subItem.targetWallet === target)
        if (filter) return true
      })
      const sourceFromAccountTypeList = accountTypeList?.filter((item) => item.value != target)
      const sourceList = sourceFromWalletTransferTypeList.map((item) => {
        const transferType = item.transferTypes?.find((tfItem) => tfItem.targetWallet == target)
        const account = sourceFromAccountTypeList?.find((account) => account.value == item.walletType?.walletType)
        return { ...transferType, ...item.walletType, ...account }
      })

      const targetFromWalletTransferTypeList = walletTransferTypeList.filter(
        (item) => source != item.walletType?.walletType
      )
      const targetWalletTypeList = walletTransferTypeList.find((item) => source == item.walletType?.walletType)
      const targetFromAccountTypeList = accountTypeList?.filter((item) => item.value != source)

      const targetList = targetWalletTypeList?.transferTypes?.map((item) => {
        const account = targetFromAccountTypeList?.find((accountItem) => accountItem.value == item.targetWallet)
        const targetWalletType = targetFromWalletTransferTypeList.find(
          (tfItem) => tfItem?.walletType?.walletType == item.targetWallet
        )

        return {
          ...item,
          ...account,
          ...targetWalletType?.walletType,
        }
      })
      return { sourceList, targetList }
    }
    return { sourceList: [], targetList: [] }
  }, [walletTransferType?.data?.data, accountTypeList, target, source])

  // 币种列表
  const coinList = useMemo(() => {
    return coinListData?.data?.data?.transferCoinInfoList?.map((item) => {
      return {
        ...item,
        value: item.coinId,
        label: item.shortName,
      }
    })
  }, [coinListData])
  // 当前选中币种
  const currentCoin = useMemo(() => {
    if (!coinId || !coinList) return null
    return coinList.find((item) => item.coinId === coinId)
  }, [coinId, coinList])

  // 划转
  const { mutateAsync: transfer } = usePostV2TransferMutation()
  const onTransfer = async () => {
    if (amount === '' || Number(amount) === 0) {
      notification.error({
        message: localeMap.amountNonZero,
      })
      return
    }
    if (Number(amount) > Number(currentCoin?.available)) {
      notification.error({
        message: localeMap.notEnoughBalance,
      })
      return
    }

    return transfer({
      amount,
      coinId,
      tradeId,
      coinName: currentCoin?.shortName,
      type,
    })
  }

  // 账户反转
  const reverseAccount = () => {
    const targetVal = target

    setTarget(source)
    setSource(targetVal)
  }

  // 计算当前可用
  const totalBalance = useMemo(() => {
    if (!walletInfo || !currentCoin) return 0
    return spliceDecimal(walletInfo.available, currentCoin.transferPrecision)
  }, [walletInfo, currentCoin])

  // 币种搜索
  const filterCoin = (keyword: string, option) => {
    return option.shortName.toLocaleUpperCase().indexOf(keyword.toLocaleUpperCase()) !== -1
  }

  return {
    coinId,
    setCoinId,
    coinList,
    currentCoin,
    source,
    setSource,
    target,
    setTarget,
    amount,
    setAmount,
    tradeId,
    setTradeId,
    tradeList,
    sourceList,
    targetList,
    walletInfo,
    type,
    onTransfer,
    reverseAccount,
    totalBalance,
    filterCoin,
  }
}

export default useTransferData
